import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  Accordion,
  ExternalLink,
  Notification,
  ScrollErrorForm,
  ConfirmationCheckbox,
} from 'components'
import { Variant as NotificationVariant } from 'components/Notification'
import {
  getParticipantFileUploadExpandedItems,
  setParticipantFileUploadExpandedItems,
} from 'local-storage'
import { RecordTypes } from 'flatfile-config'
import { useHistory, useLocation, Redirect } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { Spinner } from 'lp-components'
import { useParticipantsSummaryBD } from 'utils'
import * as actions from '../actions'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { ParticipantsSummaryTable, FlowActions } from '../components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Path } from 'config/portal'
import { ReasonForDiscard } from 'config/automatic-rollovers'
import { Icon as DownloadIcon } from 'images/download.svg'
import { FlatfileProvider } from '@flatfile/react'
import FlatfileUpload from '../../../components/FlatfileUpload'
import { Icon as ArrowDownIcon } from 'images/utility-icons/arrow-down.svg'
import { AgreementsMapping, ProductTypes } from 'config/products'
import getUploadParticipantAccordionItems from '../../products/components/getUploadParticipantAccordionItems'
import { usePlanMatchesEffect } from '../../products/hooks/usePlanMatchesEffect'

const propTypes = {
  legaleseConfirmationForm: PropTypes.object,
  setLegaleseConfirmationForm: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  setParticipantsForSubmission: PropTypes.func.isRequired,
  resetDiscardedParticipants: PropTypes.func.isRequired,
  participantFileName: PropTypes.string,
  setParticipantFileName: PropTypes.func.isRequired,
  fetchCompletedServicesAgreements: PropTypes.func.isRequired,
  completedServicesAgreements: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  selectedPaymentMethod: PropTypes.string,
  fetchPlanMatches: PropTypes.func.isRequired,
}

const defaultProps = {
  legaleseConfirmationForm: {},
  completedServicesAgreements: null,
  participantFileName: null,
}
const accordionItems = getUploadParticipantAccordionItems()

function UploadParticipantFileView({
  legaleseConfirmationForm,
  setLegaleseConfirmationForm,
  participantsForSubmission,
  setParticipantsForSubmission,
  resetDiscardedParticipants,
  participantFileName,
  setParticipantFileName,
  fetchCompletedServicesAgreements,
  completedServicesAgreements,
  selectedPaymentMethod,
  fetchPlanMatches,
}) {
  const history = useHistory()
  const location = useLocation()
  const [errorMessage, setErrorMessage] = useState(null)
  const [error, setError] = useState(null)
  const formRef = useRef()
  const { data: participantsSummaryData } = useParticipantsSummaryBD(
    participantsForSubmission
  )
  const [expandedItems, setExpandedItems] = useState(
    getParticipantFileUploadExpandedItems() || []
  )
  const [showAccordion, setShowAccordion] = useState(false)

  useEffect(() => {
    async function getCompletedServicesAgreements() {
      try {
        await fetchCompletedServicesAgreements(
          AgreementsMapping.BD_PLAN_MATCHING
        )
      } catch {
        setError(true)
      }
    }
    getCompletedServicesAgreements()
  }, [])

  useEffect(() => {
    if (!isEmpty(participantsForSubmission)) {
      setShowAccordion(false)
    }
  }, [participantsForSubmission])

  usePlanMatchesEffect({
    participantsForSubmission,
    fetchPlanMatches,
    productType: ProductTypes.BENEFIT_DISTRIBUTIONS,
    setError,
  })

  const handleToggleAccordion = () => {
    setShowAccordion(!showAccordion)
  }

  const handleSubmit = async (values) => {
    // if, for instance, flatfile error exists, do nothing
    if (errorMessage) return
    if (isEmpty(participantsForSubmission)) {
      setErrorMessage('Upload a valid file to continue')
      return
    }
    setParticipantFileUploadExpandedItems(expandedItems) // set expanded items in localStorage
    setLegaleseConfirmationForm(values)
    history.push({
      pathname: '/benefit-distributions/plan-mapping',
      state: {
        search: location.state?.search,
      },
    })
  }

  if (!completedServicesAgreements && !error) {
    return (
      <div className="upload-participant-file-view-container">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <Redirect
        push
        to={{
          pathname: '/error',
          state: {
            ignorePrompt: true, // No need to warn the user about leaving the flow
            search: location.state?.search,
          },
        }}
      />
    )
  }

  // Prevent the user from accessing the flow if the user 1) has no completed services agreements
  // or 2) did not go through the start-new-rollover modal from the table view first

  if (!completedServicesAgreements.length) {
    return (
      <Redirect
        push
        to={{
          pathname: Path.BD_SERVICES_AGREEMENTS,
          state: {
            ignorePrompt: true,
            search: location.state?.search,
          },
        }}
      />
    )
  }
  if (!location.state?.hasConfirmed) {
    return (
      <Redirect
        push
        to={{
          pathname: Path.BD_SERVICES_AGREEMENTS,
          state: {
            ignorePrompt: true,
            showStartNewRolloverDistributionModal: true,
            search: location.state?.search,
          },
        }}
      />
    )
  }

  if (!selectedPaymentMethod)
    return <Redirect to={Path.SELECT_PAYMENT_METHOD} />

  return (
    <div className="card full-height upload-participant-file-view-container">
      <div className="form-masthead">
        <h2>Start by adding your participants</h2>
      </div>
      <div className="form-section form-section--new-spreadsheet">
        <div className="form-card-content upload-container">
          <div className="form-header">
            <p>
              You can add participants by manually completing our online
              participant file or by uploading the participant data spreadsheet.
            </p>
            <Notification type={NotificationVariant.WARNING}>
              <p className="text-title--small">
                We only process distributions that are eligible for automatic
                rollover to an IRA. This typically includes participants from
                terminating defined contribution plans or unresponsive
                participants from active plans with balances of $7,000 or less.
              </p>
            </Notification>
          </div>
          <FlatfileProvider
            publishableKey={process.env.REACT_APP_FLATFILE_PK}
            config={{
              preload: true,
              resetOnClose: true,
            }}
          >
            <FlatfileUpload
              recordType={RecordTypes.BD_PARTICIPANT}
              buttonContent="Add participant information"
              //handleRecords={handleBDRecord}
              handleValidData={(validData) => {
                setParticipantsForSubmission(validData)
                setErrorMessage('')
              }}
              handleInvalidData={(discardedData) => {
                const discardedParticipantsWithReason = discardedData.map(
                  (participant) => ({
                    ...participant,
                    reasonForDiscard: ReasonForDiscard.INVALID_DATA,
                  })
                )
                resetDiscardedParticipants(discardedParticipantsWithReason)
              }}
              setFileName={setParticipantFileName}
              fileName={participantFileName}
              participantsForSubmission={participantsForSubmission}
            />
          </FlatfileProvider>

          {!!errorMessage && (
            <span className="error-message">{errorMessage}</span>
          )}
          {!isEmpty(participantsSummaryData) && (
            <ParticipantsSummaryTable
              data={participantsSummaryData}
              title={<h4>Participant file summary</h4>}
            />
          )}
        </div>
      </div>
      <div className="form-section form-section--new-spreadsheet">
        <h3>File upload instructions</h3>
        <div className="upload-instructions">
          <p>1. Download the participant data spreadsheet.</p>
          <ExternalLink
            link={
              process.env
                .REACT_APP_RSP_INSPIRAFINANCIAL_EZ_BENEFIT_DISBURSEMENT_URL
            }
            className="link-text"
            aria-label="Download the new updated spreadsheet"
          >
            Participant data spreadsheet
          </ExternalLink>
          <DownloadIcon aria-hidden="true" />
          <p>2. Complete the template and upload it above. </p>
          <button className="button-text" onClick={handleToggleAccordion}>
            View template instructions
          </button>
          <ArrowDownIcon aria-hidden="true" />
        </div>
        {showAccordion && (
          <Accordion
            items={accordionItems}
            initialItemsExpanded={['instructions']}
            onChange={(itemUuids) => setExpandedItems(itemUuids)}
          />
        )}
      </div>
      <div className="form-section">
        <div className="form-header">
          <h3 className="is-marginless">
            Important information: review before continuing
          </h3>
        </div>
        <p>
          Currently, benefit distribution services are provided for
          distributions that are eligible for rollover to an IRA.
        </p>
        <p>
          Generally, participants of terminating defined benefit plans are not
          eligible for benefit distributions, unless the plan is not covered by
          the Pension Benefit Guaranty Corporation (PBGC). This includes benefit
          distributions made in contemplation of plan termination but before the
          formal commencement of termination proceedings. It is the Plan
          Fiduciary's responsibility to determine whether the plan is covered by
          the PBGC and whether participant accounts are eligible for benefit
          distributions.
        </p>
        <p>
          Active defined benefit and active defined contribution plan
          participant accounts may be eligible for benefit distributions when
          the present value of the participant's nonforfeitable accrued benefit
          does not exceed $7,000. In limited circumstances, accounts with
          accrued benefits above $7,000 may be eligible for benefit
          distributions. It is the Plan Fiduciary's responsibility to determine
          eligibility for each participant.
        </p>
        <Formik
          initialValues={{
            confirm: false,
            ...legaleseConfirmationForm,
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object({
            confirm: Yup.boolean()
              .required('Required')
              .oneOf([true], 'Required'),
          })}
          innerRef={formRef}
        >
          <ScrollErrorForm>
            <ConfirmationCheckbox
              name="confirm"
              label="I acknowledge that I have read and understand the information above."
            />
          </ScrollErrorForm>
        </Formik>
      </div>
      <FlowActions
        handleSubmit={() => formRef.current.handleSubmit()}
        handleBack={() => history.push(Path.SELECT_PAYMENT_METHOD)}
      />
    </div>
  )
}

UploadParticipantFileView.propTypes = propTypes

UploadParticipantFileView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    legaleseConfirmationForm: selectors.legaleseConfirmationForm(state),
    participantsForSubmission: selectors.participantsForSubmission(state),
    participantFileName: selectors.participantFileName(state),
    completedServicesAgreements: selectors.completedServicesAgreements(state),
    selectedPaymentMethod: selectors.selectedPaymentMethod(state),
  }
}

const mapDispatchToProps = {
  setLegaleseConfirmationForm: actions.setLegaleseConfirmationForm,
  setParticipantsForSubmission: actions.setParticipantsForSubmission,
  resetDiscardedParticipants: actions.resetDiscardedParticipants,
  setParticipantFileName: actions.setParticipantFileName,
  fetchCompletedServicesAgreements: apiActions.fetchCompletedServicesAgreements,
  fetchPlanMatches: apiActions.fetchPlanMatches,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  UploadParticipantFileView
)
